// UX-22 — composant de modale PARTAGÉ (confirmation/formulaire court), fondation
// pour de futurs tickets de migration (`window.confirm()` de `Children.tsx`, etc. —
// PAS traités ici, ce ticket construit UNIQUEMENT le composant). Vérifié AVANT
// d'écrire quoi que ce soit (Étape 0) : aucun mécanisme de modale/dialogue partagé
// n'existait déjà sous un autre nom — `role="dialog"`/`aria-modal="true"`
// apparaissaient déjà à plusieurs endroits (`Sidebar.tsx`, tiroir mobile ;
// `Students.tsx`/`Enrollments.tsx`, modales de confirmation web-admin), mais
// chacun est une implémentation BESPOKE propre à sa page — aucun n'a de piège de
// focus ni de fermeture au clavier (Échap), les deux exigences réelles de ce
// ticket. Réutilise le style déjà établi par ces implémentations existantes
// (overlay `bg-black/40`, `z-50`, clic sur l'overlay ferme, `stopPropagation` sur
// le contenu) plutôt que d'inventer une seconde convention visuelle.
import { useId, type ReactNode } from 'react';
import { Button } from './Button';
import { useFocusTrap } from './useFocusTrap';

export interface ModalProps {
  title: string;
  /** Corps de la modale — texte de confirmation ou formulaire court. */
  children: ReactNode;
  onCancel: () => void;
  /**
   * UX-38 — optionnel : ABSENT, le pied ne rend QUE le bouton "Annuler" (mode
   * "conteneur pur", pour un formulaire multi-étapes qui gère lui-même ses
   * propres boutons de soumission dans `children` — ex. `NewEnrollmentModal`,
   * qui embarque le flux recherche→sélection→confirmation d'`Enroll.tsx`).
   * PRÉSENT, comportement STRICTEMENT INCHANGÉ (les deux boutons, comme avant
   * ce ticket) — changement additif, aucun appelant existant ne passe
   * `onConfirm: undefined`, vérifié dans `Modal.test.tsx` avant ce changement.
   */
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
}

/**
 * `Modal` — confirmation/formulaire court. Toujours rendue par l'appelant de
 * façon CONDITIONNELLE (`{open ? <Modal ... /> : null}`, même convention que
 * les modales bespoke déjà existantes) — pas de prop `open` interne, ce
 * composant ne gère jamais sa propre visibilité.
 *
 * Exemple d'usage (à suivre par les futurs tickets de migration) :
 *
 * ```tsx
 * const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
 *
 * {confirmingDeleteId ? (
 *   <Modal
 *     title="Supprimer cet enfant ?"
 *     onCancel={() => setConfirmingDeleteId(null)}
 *     onConfirm={() => {
 *       setConfirmingDeleteId(null);
 *       void handleDelete(confirmingDeleteId);
 *     }}
 *     confirmLabel="Supprimer"
 *   >
 *     <p>Cette action est irréversible.</p>
 *   </Modal>
 * ) : null}
 * ```
 */
export function Modal({ title, children, onCancel, onConfirm, confirmLabel = 'Confirmer', cancelLabel = 'Annuler' }: ModalProps) {
  // UX-77 — piège de focus + Échap extraits en hook partagé (`useFocusTrap`),
  // réutilisé tel quel par les ex-tiroirs `EnrollmentDrawer`/`StudentDrawer` (pages depuis UX-81/UX-80)/
  // `AssignClassModal`/`RejectModal` (web-admin). Comportement STRICTEMENT
  // inchangé pour cette `Modal` elle-même — voir `useFocusTrap.ts` pour le
  // détail du mécanisme (focus initial, cycle Tab/Shift+Tab, Échap,
  // restauration du focus).
  const dialogRef = useFocusTrap<HTMLDivElement>(onCancel);
  const titleId = useId();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
      data-testid="modal-backdrop"
    >
      {/*
       * UX-69 — la carte est désormais un conteneur flex en COLONNE, plafonné à
       * `calc(100vh-2rem)` (les 2 × `p-4` = 2rem de l'overlay ci-dessus, box-sizing
       * border-box) : titre et pied restent `shrink-0` (TOUJOURS visibles, jamais
       * poussés hors écran), seul le corps (`min-h-0 flex-1 overflow-y-auto`) défile
       * si le formulaire dépasse la hauteur disponible. `min-h-0` est nécessaire —
       * sans lui, un enfant `flex-1` garde sa hauteur `min-height: auto` (taille de
       * son contenu) et ne se laisse jamais comprimer par le plafond du parent,
       * rendant `overflow-y-auto` inopérant (piège flexbox classique). Sans
       * dépassement (formulaire court, desktop), la carte se dimensionne
       * simplement à son contenu — `max-h` ne s'applique qu'en PLAFOND, jamais un
       * agrandissement forcé : aucun scroll ni espace vide ajouté.
       */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // Empêche le clic À L'INTÉRIEUR de la carte de remonter jusqu'à
        // l'overlay (qui fermerait la modale) — même pattern déjà établi par
        // les modales bespoke existantes (`Enrollments.tsx`).
        onClick={(event) => event.stopPropagation()}
        className="ss-modal flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col rounded-[10px] border border-border bg-white shadow-lg"
        data-testid="modal"
      >
        <h2 id={titleId} className="shrink-0 px-6 pb-3 pt-6 text-lg font-semibold text-text">
          {title}
        </h2>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 text-sm text-text-secondary" data-testid="modal-body">
          {children}
        </div>
        <div className="flex shrink-0 justify-end gap-2 px-6 pb-6" data-testid="modal-footer">
          <Button type="button" variant="secondary" onClick={onCancel} data-testid="modal-cancel">
            {cancelLabel}
          </Button>
          {onConfirm ? (
            <Button type="button" variant="primary" onClick={onConfirm} data-testid="modal-confirm">
              {confirmLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
